'use client'

import { ComponentSpinner } from '@tebuto/layout/Spinner'
import { cn } from '@tebuto/lib/utils'
import type { ReactNode } from 'react'

export interface PaymentMethodChoiceProps {
    label: string
    description?: string
    selected: boolean
    icon?: ReactNode
    children?: ReactNode
    disabled?: boolean
    loading?: boolean
    error?: string | null
    onSelect: () => void
    testId: string
    badge?: string
}

export function PaymentMethodChoice({
    label,
    description,
    selected,
    icon,
    children,
    disabled = false,
    loading = false,
    error = null,
    onSelect,
    testId,
    badge
}: PaymentMethodChoiceProps) {
    const isDisabled = disabled || loading

    return (
        <div
            data-testid={testId}
            className={cn(
                'w-full overflow-hidden rounded-lg border text-left transition-colors',
                'widget-bg-field',
                selected ? 'border-2 border-(--widget-text-primary)' : 'border widget-border',
                isDisabled && 'opacity-50'
            )}
        >
            <button
                type="button"
                onClick={onSelect}
                disabled={isDisabled}
                className={cn(
                    'widget-offline-payment-row transition-colors',
                    selected && 'widget-offline-payment-row--selected',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--widget-primary-600) focus-visible:ring-inset',
                    isDisabled && 'cursor-not-allowed'
                )}
            >
                {icon ? <span className="widget-offline-payment-row__icon">{icon}</span> : null}
                <span className="widget-offline-payment-row__label">{label}</span>
                {badge || loading ? (
                    <span className="widget-offline-payment-row__trailing">
                        {badge ? (
                            <span className="rounded-full px-2 py-0.5 text-xs widget-bg-primary-100 widget-text-secondary">
                                {badge}
                            </span>
                        ) : null}
                        {loading ? <ComponentSpinner className="size-5 widget-primary-600" /> : null}
                    </span>
                ) : null}
            </button>
            {description ? <p className="px-4 pb-3 text-sm widget-text-secondary">{description}</p> : null}
            {error ? <p className="px-4 pb-3 text-xs text-red-600">{error}</p> : null}
            {selected && children ? (
                <div className="px-4 pb-4" style={{ paddingLeft: 'var(--widget-payment-method-label-inset)' }}>
                    {children}
                </div>
            ) : null}
        </div>
    )
}
